// © Microsoft Corporation. All rights reserved.

/** @jsx h */ /* Note: Required to be at the top of the file along with the import h */
import h from "@msnews/wc-jsx";
import React from "react";

import { BaseExperience, InitialCommonExperienceProps, blackHexColor, whiteHexColor } from "@msnews/experiences-base";
import { CommonHeaderHandledProps, CommonHeaderUnhandledProps, ContextualNavItem } from "./CommonHeader.props";

import { MicrosoftLogoIcon, newMicrosoftLogoIcon } from "./CommonHeader.icons";
import { FeedNavigation, FeedNavigationData } from "@msnews/feed-navigation";
import { HandledPropsKeys } from "@msnews/core";
import { DesignSystem, neutralLayerFloating, DesignSystemDefaults, neutralLayerCardContainer } from "@microsoft/fast-components-styles-msft";

import { CommonHeaderProps } from "./CommonHeader.props";
import { SearchBox } from "@msnews/search-box";
import { CommonHeaderRenderError } from "@msnews/diagnostics";
import { compareDisplayNameDesc, KeyMetricTags, windowsMarkets } from "@msnews/experiences-constants";
import { TelemetryActionType, TelemetryBehaviorType, Telemetry } from "@msnews/telemetry-contracts";
import { FluentDivider, FluentDesignSystemProvider } from "@fluentui/web-components";
import { AutoSuggestDelayBinding, searchBoxId, AutoSuggestInitProperties, initializeAutoSuggest, searchInputId } from "@msnews/autosuggest";
import { Direction } from "@microsoft/fast-web-utilities";
import { DesignSystemProvider } from "@microsoft/fast-jss-manager-react";
import { MeControlComponent } from "@msnews/me-control-component";
import { MsftHamburgerMenu, MsftHamburgerMenuItem } from "@fast/components";
import { Settings, SettingsProps as BaseSettingsProps } from "@msnews/settings";
import { TelemetryDOM } from "@msnews/telemetry-core";
import { getAppEnvironment } from "@msnews/core";

// Prevent tree shaking
FluentDesignSystemProvider;
FluentDivider;
MsftHamburgerMenuItem;

const languageSelectorId = "languageSelector";

/**
 * Common header experience
 */
export class CommonHeader extends BaseExperience<CommonHeaderHandledProps, CommonHeaderUnhandledProps, CommonHeaderComponentState> {
    /** The CommonHeader default prop contract. Describes any default values for any component handled props */
    public static defaultProps: Partial<CommonHeaderHandledProps> = {
        experienceName: "CommonHeader"
    };

    /** Ref for language selector */
    private marketSelectorRef: HTMLElement;

    /** Sorted list of markets for the language selector */
    private markets = windowsMarkets.sort(compareDisplayNameDesc);

    /** An enumeration of all handled props. All props passed to the component that are not enumerated here will be treated as unhandled props */
    protected handledProps: HandledPropsKeys<CommonHeaderHandledProps> = {
        ...InitialCommonExperienceProps,
        managedClasses: undefined,
        hamburgerMenuProps: undefined,
        contextualNavProps: undefined,
        contextualSecondaryNavProps: undefined,
        isContextualSecondaryNavEnabled: undefined,
        searchProps: undefined,
        meControlProps: undefined,
        handleRenderInfo: undefined,
        selectedNavItem: undefined,
        selectedSecondaryNavItem: undefined,
        selectedNavMenuItem: undefined,
        selectedSecondaryNavMenuItem: undefined,
        goToMyFeed: undefined,
        disableFirstRow: undefined,
        isWindowsShellExperience: undefined,
        hideSecondaryNavAsL3InContent: undefined,
        primeOverrideVerticalKey: undefined,
        hidePersonalizeButton: undefined,
        appErrorCallback: undefined,
        settingsProps: undefined,
        disableDarkmode: undefined,
        includeWrapperDesignSystemProvider: undefined,
        commonHeaderTelemetryContext: undefined,
        logoAltText: undefined,
        showNewLogo: undefined,
        layout: undefined,
        useSearchBoxAnaheimUx: undefined,
        useSettingsIconFluentVersion: undefined,
        swapAccountAndSettingsIcon: undefined
    };

    /** HTML Element for search box input element */
    protected inputRef: HTMLElement;

    /** HTML Element for search box form element */
    protected formRef: HTMLElement;

    /** Autosuggest event handler */
    protected autoSuggestEventHandler: EventListener;

    /** Autosuggest register on search handler */
    protected registerAutoSuggestOnSearchEventHandler: EventListener;

    /** Autosuggest suggestion click handler */
    protected onAutoSuggestSuggestionClickHandler: EventListener;

    /** Listener for keypress events */
    protected onKeypressHandler: EventListener;

    /**
     * Reference to current design system
     */
    protected currentDesignSystem: DesignSystem;

    constructor(props: CommonHeaderProps) {
        super(props);

        if (props.disableDarkmode) {
            this.state = {
                isDarkMode: false
            };
            return;
        }

        const darkModeQuery = matchMedia("(prefers-color-scheme:dark)");

        this.state = {
            isDarkMode: darkModeQuery.matches
        };

        // Handle dark mode toggles
        darkModeQuery.addEventListener("change", (e: MediaQueryListEvent) => {
            this.setState({ isDarkMode: e.matches });
        });
    }

    /**
     * React life-cycle method called when updates are received after initial render
     */
    public componentDidUpdate(prevProps: CommonHeaderProps): void {
        super.componentDidUpdate();

        if (this.props.disableFirstRow) {
            return;
        }

        const { searchProps } = prevProps;
        if (searchProps && searchProps.autoSuggest && searchProps.autoSuggest.delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady) {
            const autoSuggestInitProps = this.getAutoSuggestInitProps();
            initializeAutoSuggest(autoSuggestInitProps);
        }
    }

    /**
     * Adding behaviors that require common header in dom here.
     */
    public componentDidMount(): void {
        super.componentDidMount();

        // Only set event listeners and autosuggest for search if top row is not disabled
        if (this.props.disableFirstRow) {
            return;
        }

        // Set refs and event listeners
        this.inputRef = document.getElementById(searchInputId);
        this.formRef = document.getElementById(searchBoxId);

        const { searchProps } = this.props;
        if (searchProps.keyPressCaptureEnabled) {
            this.onKeypressHandler = this.onKeypress.bind(this);
            document.body.addEventListener("keypress", this.onKeypressHandler, { capture: true });
        }

        // Try initializing autosuggest
        const delayBind = searchProps.autoSuggest && searchProps.autoSuggest.delayBind;
        if (delayBind
            && ((delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady
                && !searchProps.autoSuggest.businessSearchResultsEnabled)
                || delayBind !== AutoSuggestDelayBinding.OnBusinessSearchResultsReady)) {
            const autoSuggestInitProps = this.getAutoSuggestInitProps();
            initializeAutoSuggest(autoSuggestInitProps);
        }

        // set the TTF marker for search box
        this.markTimeToFunctional(KeyMetricTags.searchBox);

        // Using raf because elementtiming is only supported for text elements and images. All child web components either don't use
        // these types of elements or have them deep in the shadow dom
        window.requestAnimationFrame(() => {
            setTimeout(() => {
                this.markTimeToVisuallyReady(true);
                this.markTimeToVisuallyReady(true, undefined, KeyMetricTags.searchBox);
                Telemetry.observeSingleElement(this.formRef.parentElement);
            });
        });
    }

    /**
     * Uber handler that gets invoked for unhandled errors.
     * @param {Error} error error object.
     */
    public componentDidCatch(error: Error): void {
        const { appErrorCallback } = this.props;
        if (!appErrorCallback) {
            return;
        }

        appErrorCallback({
            ...CommonHeaderRenderError,
            message: "Render error in common header",
            pb: {
                ...CommonHeaderRenderError.pb,
                customMessage: `${error.message}\n${error.stack}`
            }
        });
    }

    /**
     * Render the experience.
     * @return {JSX.Element}
     */
    public render(): JSX.Element {
        super.render();

        const { managedClasses, meControlProps, disableFirstRow, useSearchBoxAnaheimUx, useSettingsIconFluentVersion, swapAccountAndSettingsIcon, commonHeaderTelemetryContext } = this.props;
        const designSystem = this.createDesignSystem();
        const meControlTelemetryTag = commonHeaderTelemetryContext && commonHeaderTelemetryContext.meControlTag;
        const meControlComponentProps = {
            ...meControlProps,
            useDarkTheme: this.state.isDarkMode,
            telemetryTag: meControlTelemetryTag
        };
        const styles = {
            backgroundColor: designSystem.backgroundColor
        };

        let layoutClass = "";
        if (this.props.layout === "constrain") {
            layoutClass = ` ${managedClasses.constrainLayout}`;
        } else if (this.props.layout === "stretch") {
            layoutClass = ` ${managedClasses.stretchLayout}`;
        }
        const rootContainerClasses = managedClasses.rootContainer + layoutClass;

        let settingsContainerClasses = managedClasses.settingsContainer;
        if (useSettingsIconFluentVersion) {
            settingsContainerClasses += ` ${managedClasses.fluentSettingsIcon}`;
        }
        if (swapAccountAndSettingsIcon) {
            settingsContainerClasses += ` ${managedClasses.swapAccountAndSettings}`;
        }

        const commonHeader = (
            <FluentDesignSystemProvider use-defaults props={{ ...designSystem }}>
                <div className={managedClasses.root}
                    style={styles}
                    {...this.unhandledProps()}
                    role="banner"
                >
                    <div className={rootContainerClasses}>
                        {!disableFirstRow &&
                            <div className={managedClasses.topContainer}>
                                {this.renderLogo()}
                                <div className={managedClasses.searchContainer} role="search">{this.renderSearch()}</div>
                                <div className={managedClasses.userPreferencesContainer + (useSearchBoxAnaheimUx ? ` ${managedClasses.useSearchboxAnaheimUx}` : "")}>
                                    {swapAccountAndSettingsIcon ?
                                        <React.Fragment>
                                            <div className={settingsContainerClasses}>
                                                {this.renderSettings()}
                                            </div>
                                            <div className={managedClasses.meControlContainer} {...meControlTelemetryTag}>
                                                <MeControlComponent {...meControlComponentProps} />
                                            </div>
                                        </React.Fragment> :
                                        <React.Fragment>
                                            <div className={managedClasses.meControlContainer} {...meControlTelemetryTag}>
                                                <MeControlComponent {...meControlComponentProps} />
                                            </div>
                                            <div className={settingsContainerClasses}>
                                                {this.renderSettings()}
                                            </div>
                                        </React.Fragment>
                                    }
                                </div>
                            </div>
                        }
                        <div className={managedClasses.navigationContainer}>
                            {this.renderHamburgerMenu()}
                            <div className={managedClasses.contextualNavContainer}>{this.renderContextualNav()}</div>
                        </div>
                        {this.renderContextualSecondaryNav()}
                    </div>
                    {this.renderDivider()}
                </div>
            </FluentDesignSystemProvider>
        );

        if (this.props.includeWrapperDesignSystemProvider) {
            return (
                <DesignSystemProvider designSystem={designSystem}>
                    {commonHeader}
                </DesignSystemProvider>
            );
        } else {
            return commonHeader;
        }
    }

    public renderDivider(): JSX.Element {
        const { isContextualSecondaryNavEnabled } = this.props;
        if (!isContextualSecondaryNavEnabled) {
            return (<FluentDivider></FluentDivider>);
        }

        return null;
    }

    /**
     * Removes all event listeners
     */
    public componentWillUnmount(): void {
        const { searchProps } = this.props;
        if (searchProps && searchProps.keyPressCaptureEnabled && this.onKeypressHandler) {
            document.body.removeEventListener("keypress", this.onKeypressHandler);
        }
    }

    /**
     * Renders the hamburger menu with the passed in props
     */
    private renderHamburgerMenu(): JSX.Element {
        const { hamburgerMenuProps, handleRenderInfo, commonHeaderTelemetryContext, managedClasses, isContextualSecondaryNavEnabled } = this.props;
        if (!hamburgerMenuProps) {
            return null;
        }
        const { menuButtonTooltip, closeButtonTooltip, navItems, selectedNavId } = hamburgerMenuProps;
        const { hamburgerMenuTelemetry, closeHamburgerMetaTag, hamburgerMenuExpandedChangeTelemetryCallback } = commonHeaderTelemetryContext;

        if (isContextualSecondaryNavEnabled && !this.props.isWindowsShellExperience) {
            return null;
        }

        return <div className={managedClasses.hamburgerMenuContainer}>
            <MsftHamburgerMenu
                use-mobile-styles={false}
                menu-button-tooltip={menuButtonTooltip}
                close-button-tooltip={closeButtonTooltip}
                props={{
                    navItems,
                    selectedNavId,
                    hamburgerMenuButtonTelemetryTag: hamburgerMenuTelemetry && hamburgerMenuTelemetry.getMetadataTag(),
                    closeButtonTelemetryTag: closeHamburgerMetaTag
                }}
                events={{
                    change: handleRenderInfo,
                    "expanded-change": hamburgerMenuExpandedChangeTelemetryCallback
                }}
            ></MsftHamburgerMenu></div>;
    }

    /**
     * Renders the contextual nav with the passed in props
     */
    private renderContextualNav(): JSX.Element {
        const { contextualNavProps, handleRenderInfo, selectedNavItem, selectedNavMenuItem, commonHeaderTelemetryContext, isContextualSecondaryNavEnabled, hidePersonalizeButton, isWindowsShellExperience, primeOverrideVerticalKey } = this.props;
        if (!contextualNavProps) {
            return null;
        }
        const { navGroups, localizedStrings, interestsNavData } = contextualNavProps;
        const { overflowMetaTag, contextualNavTelemetry, personalizeButtonMetaTag } = commonHeaderTelemetryContext;
        const contextualNavTags = contextualNavTelemetry && contextualNavTelemetry.render();
        const isSportsVerticalOutsideWindowShellExperience = (!isWindowsShellExperience && primeOverrideVerticalKey === "sports");

        return <FeedNavigation
            props={{
                navGroups,
                selectedNavItem,
                selectedNavMenuItem,
                interestsNavData,
                localizedStrings,
                hidePersonalizeButton: hidePersonalizeButton || isSportsVerticalOutsideWindowShellExperience,
                overflowTelemetryTag: overflowMetaTag,
                displayBreadCrumb: !isContextualSecondaryNavEnabled,
                showPipeSeparator: true,
                personalizeButtonTelemetryTag: personalizeButtonMetaTag
            }}
            events={{ change: handleRenderInfo }}
            {...contextualNavTags}
        >
        </FeedNavigation>;
    }

    /**
     * Renders the contextual nav with the passed in props
     */
    private renderContextualSecondaryNav(): JSX.Element {
        // TODO: do we need to tweak any telemetry
        const { contextualSecondaryNavProps, handleRenderInfo, selectedNavItem, selectedSecondaryNavItem, selectedSecondaryNavMenuItem, commonHeaderTelemetryContext, isContextualSecondaryNavEnabled, managedClasses, isWindowsShellExperience, hideSecondaryNavAsL3InContent } = this.props;
        if (!isContextualSecondaryNavEnabled || selectedSecondaryNavMenuItem == null || hideSecondaryNavAsL3InContent) {
            return null;
        }

        const { navGroups, localizedStrings, interestsNavData } = contextualSecondaryNavProps;
        const areNavMenuAndNavItemSame = selectedNavItem.id != selectedSecondaryNavMenuItem.id;
        const contextualNavTags = commonHeaderTelemetryContext && commonHeaderTelemetryContext.contextualNavTelemetry && commonHeaderTelemetryContext.contextualNavTelemetry.render();
        const secondaryNavContainerClasses = managedClasses.contextualSecondaryNavContainer + (isWindowsShellExperience ? " " + managedClasses.contextualSecondaryNavContainerPadding : "");
        return <div className={secondaryNavContainerClasses}>
            <div className={managedClasses.secondaryFeedNavParent}>
                <FeedNavigation
                    props={{
                        navGroups,
                        selectedNavItem: selectedSecondaryNavItem,
                        selectedNavMenuItem: selectedSecondaryNavMenuItem,
                        interestsNavData,
                        localizedStrings,
                        hidePersonalizeButton: true,
                        displayBreadCrumb: areNavMenuAndNavItemSame,
                        showOneParentBreadCrumb: areNavMenuAndNavItemSame,
                        showPipeSeparator: false
                    }}
                    events={{ change: handleRenderInfo }}
                    {...contextualNavTags}
                >
                </FeedNavigation>
            </div>
        </div>;
    }

    /**
     * Renders the search box with the passed in components
     */
    private renderSearch(): JSX.Element {
        const { searchProps } = this.props;
        // Don't render if there are no props
        if (!searchProps) {
            return null;
        }

        const { action, autoFocus, autoComplete, formParameters, isOnImage, maxLength, minLength, placeholder, readOnly, spellCheck, target, title, telemetryContext, buttonLabel, useAnaheimUx } = searchProps;
        const searchBoxProps = {
            action,
            autoFocus,
            autoComplete,
            dataCustomhandled: true,
            formParameters,
            isOnImage,
            maxLength,
            minLength,
            placeholder,
            readOnly,
            spellCheck,
            target,
            title,
            id: searchInputId,
            buttonLabel,
            useAnaheimUx,
            buttonTelemetryTag: telemetryContext.buttonMetaTag,
            inputTelemetryTag: telemetryContext.inputTag,
            onSubmit: this.onSearchSubmit,
            ...(telemetryContext.formTag)
        };

        return <SearchBox {...searchBoxProps}></SearchBox>;
    }

    /**
     * Renders the settings button and dialog
     */
    private renderSettings(): JSX.Element {
        const { settingsProps, useSettingsIconFluentVersion, commonHeaderTelemetryContext } = this.props;
        if (!settingsProps) {
            return null;
        }

        const { goToPersonalize, isCnDomain, localizedStrings, marketSelectorProps } = settingsProps;
        const { settingsMetaTag, settingsPersonalizeButtonMetaTag, settingsCloseButtonMetaTag } = commonHeaderTelemetryContext;
        return <Settings
            props={{
                goToPersonalize,
                isCnDomain,
                localizedStrings,
                marketSelectorProps,
                telemetryContext: {
                    settingsMetaTag,
                    personalizeButtonMetaTag: settingsPersonalizeButtonMetaTag,
                    settingsCloseButtonMetaTag
                }
            }}
        >
            {useSettingsIconFluentVersion &&
                <span slot="glyph">
                    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.91075 7.38266C2.28004 6.24053 2.88839 5.19213 3.69109 4.30364C3.82683 4.15339 4.03978 4.09984 4.23044 4.16802L6.14873 4.85392C6.6688 5.03977 7.24107 4.76883 7.42692 4.24875C7.4452 4.19762 7.45927 4.14507 7.469 4.09173L7.83446 2.08573C7.8708 1.88627 8.02398 1.7285 8.22227 1.6863C8.80246 1.5628 9.39734 1.5 10 1.5C10.6023 1.5 11.1968 1.56273 11.7767 1.68607C11.9749 1.72824 12.1281 1.88591 12.1645 2.08529L12.531 4.09165C12.6301 4.63497 13.1509 4.9951 13.6942 4.89601C13.7476 4.88627 13.8002 4.87219 13.8512 4.85395L15.7696 4.16802C15.9602 4.09984 16.1732 4.15339 16.3089 4.30364C17.1116 5.19213 17.72 6.24053 18.0893 7.38266C18.1516 7.57534 18.0915 7.78658 17.9371 7.91764L16.3823 9.23773C15.9613 9.5952 15.9098 10.2263 16.2673 10.6473C16.3024 10.6887 16.3409 10.7271 16.3823 10.7623L17.9371 12.0824C18.0915 12.2134 18.1516 12.4247 18.0893 12.6173C17.72 13.7595 17.1116 14.8079 16.3089 15.6964C16.1732 15.8466 15.9602 15.9002 15.7696 15.832L13.8513 15.1461C13.3312 14.9602 12.759 15.2312 12.5731 15.7512C12.5548 15.8024 12.5408 15.8549 12.531 15.9085L12.1645 17.9147C12.1281 18.1141 11.9749 18.2718 11.7767 18.3139C11.1968 18.4373 10.6023 18.5 10 18.5C9.39734 18.5 8.80246 18.4372 8.22227 18.3137C8.02398 18.2715 7.8708 18.1137 7.83446 17.9143L7.46902 15.9084C7.36993 15.365 6.84916 15.0049 6.30583 15.104C6.25241 15.1137 6.19987 15.1278 6.14881 15.1461L4.23044 15.832C4.03978 15.9002 3.82683 15.8466 3.69109 15.6964C2.88839 14.8079 2.28004 13.7595 1.91075 12.6173C1.84845 12.4247 1.90852 12.2134 2.06289 12.0824L3.61773 10.7623C4.03872 10.4048 4.09021 9.77373 3.73274 9.35274C3.69759 9.31135 3.65913 9.27288 3.61775 9.23775L2.06289 7.91764C1.90852 7.78658 1.84845 7.57534 1.91075 7.38266ZM2.9713 7.37709L4.26499 8.47546C4.34778 8.54576 4.42471 8.62269 4.49501 8.70548C5.20995 9.54746 5.10697 10.8096 4.26497 11.5246L2.9713 12.6229C3.26335 13.4051 3.6848 14.1322 4.21623 14.7751L5.81221 14.2044C5.91449 14.1679 6.01958 14.1397 6.12643 14.1202C7.21307 13.922 8.25462 14.6423 8.45281 15.729L8.75678 17.3975C9.16465 17.4655 9.58 17.5 10 17.5C10.4197 17.5 10.8348 17.4656 11.2424 17.3976L11.5472 15.7289C11.5667 15.6221 11.5949 15.517 11.6314 15.4147C12.0031 14.3746 13.1477 13.8327 14.1879 14.2044L15.7838 14.7751C16.3152 14.1322 16.7367 13.4051 17.0287 12.6229L15.735 11.5245C15.6522 11.4542 15.5753 11.3773 15.505 11.2945C14.7901 10.4525 14.8931 9.1904 15.7351 8.47544L17.0287 7.37709C16.7367 6.59486 16.3152 5.86783 15.7838 5.22494L14.1878 5.79559C14.0855 5.83214 13.9804 5.8603 13.8736 5.87979C12.787 6.07796 11.7454 5.3577 11.5473 4.27119L11.2424 2.60235C10.8348 2.53443 10.4197 2.5 10 2.5C9.58 2.5 9.16465 2.53448 8.75678 2.60249L8.45279 4.27105C8.43331 4.37791 8.40515 4.48299 8.3686 4.58527C7.99689 5.62542 6.85236 6.1673 5.81213 5.79556L4.21623 5.22494C3.6848 5.86783 3.26335 6.59486 2.9713 7.37709ZM7.50001 10C7.50001 8.61929 8.6193 7.5 10 7.5C11.3807 7.5 12.5 8.61929 12.5 10C12.5 11.3807 11.3807 12.5 10 12.5C8.6193 12.5 7.50001 11.3807 7.50001 10ZM8.50001 10C8.50001 10.8284 9.17159 11.5 10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17159 8.5 8.50001 9.17157 8.50001 10Z" />
                    </svg>
                </span>
            }
        </Settings>;
    }

    /**
     * Renders the logo for the common header. Will try to render from config, but fallback to default Microsoft logo
     * if it's not available.
     */
    private renderLogo(): JSX.Element {
        const { managedClasses, commonHeaderTelemetryContext, logoAltText, showNewLogo } = this.props;

        let commonHeaderLogo = <span className={managedClasses.defaultLogo}>{MicrosoftLogoIcon}</span>;
        let logoHeight = "22px";

        // TODO: set showNewLogo as default after deployment in Sept - Task 3661240
        if (showNewLogo) {
            const url = new URL(window.location.origin);
            url.pathname = `/${getAppEnvironment().CurrentMarket}/feed`;

            // Preserve any flight or spalink values in the URL
            const searchParams = new URLSearchParams(window.location.search);
            const itemParams = searchParams.getAll("item");
            if (itemParams) {
                for (const index in itemParams) {
                    url.searchParams.append("item", itemParams[index]);
                }
            }

            commonHeaderLogo = <span className={managedClasses.defaultLogo}><a href={url.toString()} aria-label={logoAltText} role="link" >{newMicrosoftLogoIcon}</a></span>;
            logoHeight = "20px";
        }

        const { logoTag } = commonHeaderTelemetryContext;

        return <div
            className={managedClasses.logo}
            title={logoAltText}
            style={{ "--common-header-logo-color": this.state.isDarkMode ? "var(--neutral-foreground-rest)" : "#737373", height: logoHeight }}
            {...logoTag}>
            {commonHeaderLogo}
        </div>;
    }

    /**
     * Handler for keypress events
     * @param event keyboard event
     */
    private onKeypress(event: React.KeyboardEvent<HTMLElement>): void {
        const target = event.target as Element;
        const targetTagName = target.tagName.toLowerCase();

        // If user is typing in another input element, ignore
        if (targetTagName === "input" || targetTagName === "textarea" || targetTagName === "msft-feed-layout" ||
            targetTagName === "card-action" || targetTagName === "qna-feed-list" || targetTagName === "div") {
            return;
        }

        // Only capture alphanumeric characters
        if (event.charCode > 32 && event.charCode <= 126 && target.id !== searchInputId) {
            this.inputRef.focus();
            event.stopPropagation();
        }
    }

    /**
     * Handler for search submit events
     */
    private onSearchSubmit = (): void => {
        const { searchProps } = this.props;
        if (!searchProps || !searchProps.telemetryCallback) {
            return;
        }

        const contentItem = TelemetryDOM.get(this.formRef);
        contentItem.contract.destinationUrl = searchProps.action;
        TelemetryDOM.setAttribute(this.formRef, contentItem.contract);
        searchProps.telemetryCallback(this.formRef, TelemetryActionType.Submit, TelemetryBehaviorType.TextSearch);
    };

    /**
     * Gets the autosuggest init props from the common header props
     */
    private getAutoSuggestInitProps(): AutoSuggestInitProperties {
        const { searchProps } = this.props;
        if (!searchProps) {
            return null;
        }

        const { autoSuggest, requestId, telemetryCallback, action } = searchProps;
        return {
            autoSuggestProperties: autoSuggest,
            requestId,
            telemetryCallback,
            action,
            searchBoxContext: this,
            isDarkMode: this.state.isDarkMode
        };
    }

    /**
     * Create design sytem for CommonHeader
     */
    private createDesignSystem(): typeof DesignSystemDefaults {
        return {
            ...DesignSystemDefaults,
            backgroundColor: this.getBackgroundColor(),
            direction: document.dir && document.dir.toLowerCase() === "rtl" ? Direction.rtl : Direction.ltr
        };
    }
    /**
    * Determine the background color based on the theme and if darkmode is disabled.
    */
    private getBackgroundColor(): string {
        return this.state.isDarkMode ?
            neutralLayerCardContainer(() => blackHexColor)(DesignSystemDefaults) :
            neutralLayerFloating(() => whiteHexColor)(DesignSystemDefaults);
    }
}

/**
 * Interface for Common Header Component State
 */
export interface CommonHeaderComponentState {
    /** Dark mode state. Controls the background color for light and dark themes */
    isDarkMode: boolean;
}

declare module "@msnews/feed-navigation" {
    interface FeedNavigation extends React.Component<{
        props: FeedNavigationData<ContextualNavItem>;
        events: any;
    } & React.HTMLAttributes<FeedNavigation>> { }
}

declare module "@msnews/settings" {
    interface Settings extends React.Component<{
        props: BaseSettingsProps;
        events?: any;
    } & React.HTMLAttributes<Settings>> { }
}

/**
 * This file needs to exist to augment the existing typings from FluentDesignSystemProvider
 * See https://www.typescriptlang.org/docs/handbook/declaration-merging.html#module-augmentation
 */
declare module "@fluentui/web-components" {
    interface FluentDesignSystemProvider extends React.Component<{
        "use-defaults"?: boolean,
        backgroundColor?: string,
        props?: any,
        direction?: string
    } & React.HTMLAttributes<FluentDesignSystemProvider>> { }

    interface FluentDivider extends React.Component<React.HTMLAttributes<FluentDivider>> { }
}

declare module "@fast/components" {
    interface MsftHamburgerMenu extends React.Component<{
        "use-mobile-styles": boolean,
        "menu-button-tooltip": string,
        "close-button-tooltip": string,
        props: {
            navItems: HamburgerMenuItemInternal[],
            selectedNavId: string,
            hamburgerMenuButtonTelemetryTag: string,
            closeButtonTelemetryTag: string
        },
        events: any
    } & React.HTMLAttributes<MsftHamburgerMenu>> { }
}
